'use client';

import { Input } from 'antd';
import React, { ReactNode } from 'react';
import Label from './Label';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import cn from 'classnames';
import { inputBaseClasses } from './Input';
import './components.scss';
import HelperText from './HelperText';
import { useTranslation } from 'react-i18next';
// import { useTranslations } from 'next-intl';

export type SingleValidation = {
  text: string;
  passed: boolean;
};

type Props = {
  id: string;
  status?: 'error' | 'success';
  disabled?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
  statusIcon?: boolean;
  CustomIcon?: ReactNode;
  validationMessages?: SingleValidation[];
  labelHelperText?: string;
} & Omit<React.ComponentProps<typeof Input.Password>, 'status'>;

const PasswordInput = ({
  status,
  disabled,
  label,
  required,
  id,
  statusIcon,
  CustomIcon,
  validationMessages,
  labelHelperText,
  helperText,
  className,
  ...props
}: Props) => {
  const { t } = useTranslation('components.passwordValidationMessages');
  const statusIcons = React.useMemo(
    () => ({
      error: (
        <InfoCircleOutlined
          className={cn('text-red-500', {
            'text-light-text-secondary dark:text-dark-text-secondary': disabled,
          })}
        />
      ),
      success: (
        <CheckCircleOutlined
          className={cn('text-green-500', {
            'text-light-text-secondary dark:text-dark-text-secondary': disabled,
          })}
        />
      ),
    }),
    [disabled]
  );

  return (
    <div className="flex flex-col">
      {label && (
        <Label
          labelHelperText={labelHelperText}
          text={label}
          required={required}
          htmlFor={id}
        />
      )}
      <Input.Password
        suffix={statusIcon && status && statusIcons[status]}
        prefix={CustomIcon}
        id={id}
        size="large"
        className={cn(inputBaseClasses, className, {
          '!border-red-500 hover:!border-red-500': status === 'error',
          '!border-green-500 hover:!border-green-500': status === 'success',
          'border-0 dark:border dark:!border-dark-text-placeholder !bg-light-button-label-primary dark:!bg-transparent':
            disabled,
          'hover:!border-pink-500': !disabled && !status,
        })}
        {...props}
      />
      {helperText && (
        <HelperText text={helperText} status={status} disabled={disabled} />
      )}
      <div className="flex flex-col mt-2">
        {validationMessages?.map(({ passed, text }) => (
          <SingleValidationMessage key={text} passed={passed} text={t(text)} />
        ))}
      </div>
    </div>
  );
};

export default PasswordInput;

const SingleValidationMessage = ({ text, passed }: SingleValidation) => {
  return (
    <p className="flex gap-2 items-center">
      <span>
        {passed ? (
          <CheckCircleOutlined className="text-dark-success" />
        ) : (
          <CloseCircleOutlined className="text-dark-warning" />
        )}
      </span>
      <span className="text-text-secondary dark:text-black-400 text-xs">
        {text}
      </span>
    </p>
  );
};
